import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { getRandomSampleModel } from '../utils/sampleModels';
import { useTracking, UI_EVENTS } from '../hooks/useTracking';
import { useGemBalance } from '../hooks/useGemBalance';

interface EhGemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitType?: 'low_gem_balance';
  action?: 'tts' | 'image' | 'video' | '';
  characterData?: {
    id?: string;
    name?: string;
    imageUrl?: string;
  };
}

export function EhGemsDialog({
  open,
  onOpenChange,
  limitType = 'low_gem_balance',
  action = '',
  characterData,
}: EhGemsDialogProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(open);
  const { trackEvent } = useTracking();
  const { balance } = useGemBalance();
  
  useEffect(() => {
    setIsOpen(open);
    if (open) {
      // Track when gems dialog is shown
      trackEvent(UI_EVENTS.SALES_GEMS_DIALOG_SHOWN, { 
        limitType,
        characterId: characterData?.id,
        characterName: characterData?.name,
        gemBalance: balance
      });
    }
  }, [open, trackEvent, limitType, characterData, balance]);
  
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange(newOpen);
  };
  
  const handleBuyGemsClick = () => {
    // Track when user clicks buy gems button
    trackEvent(UI_EVENTS.SALES_GEMS_DIALOG_CLICKED, {
      limitType,
      characterId: characterData?.id,
      characterName: characterData?.name,
      gemBalance: balance
    });
    
    // Close dialog and navigate to gems page
    handleOpenChange(false);
    navigate('/gems');
  };

  const getLimitMessage = () => {
    const characterSuffix = `${characterData?.name ? ` from ${characterData?.name}` : ''}`

    switch (action) {
      case 'tts':
        return 'Get more gems for voice notes' + characterSuffix;
      case 'image':
        return 'Get more gems for photos' + characterSuffix;
      case 'video':
        return 'Get more gems for hot videos' + characterSuffix;
      default:
        return `Get more gems for hot premium content` + characterSuffix;
    }
  };

  const randomSampleModel = getRandomSampleModel();

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800 p-0 flex flex-col md:flex-row overflow-hidden gap-0">
        {/* Character image (if available) */}
        <div className="w-full md:w-1/2 aspect-square md:aspect-auto flex items-center justify-center overflow-hidden">
          <img
            src={characterData?.imageUrl || randomSampleModel?.assetPath}
            alt={characterData?.name || 'Character'}
            className="object-cover w-full h-full"
          />
        </div>
        
        {/* Dialog content */}
        <div className="flex-1 p-6 md:w-1/2">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl flex items-center gap-2 mb-2">
              {getLimitMessage()}
            </DialogTitle>
            <DialogDescription className="text-white text-md -mb-2">
              <span>You have </span>
              <span className="font-semibold text-blue-400">{`💎 ${balance?.total.toLocaleString() || 0}`}</span>
              <span> remaining</span>
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 mb-2 text-white/80">
            <div className="text-md text-zinc-300 mb-4">
              {action === 'tts'
                ? 'Buy more gems to enjoy more voice notes.'
                : action === 'image'
                ? 'Buy more gems to enjoy more 🔥 images.'
                : 'Buy more gems to keep enjoying premium features.'
              }
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-white">💎 Enjoy gems for...</span>
              </div>
              <ul className="space-y-1 text-sm text-zinc-300">
                <li>Voice message: 0.3 gem</li>
                <li>Chat/Studio Image: 3 gems</li>
                <li>Video: 15 gems</li>
                <li>Character creation: 10 gems</li>
              </ul>
            </div>
          </div>
          
          {characterData?.name && (
            <p className="text-white font-semibold text-md mt-4">
              <span>{characterData.name} is waiting to speak with you!</span>
            </p>
          )}
          
          <DialogFooter className="mt-6">
            <div className="flex flex-col gap-2 w-full items-center">
              <Button
                type="button" 
                onClick={handleBuyGemsClick}
                className="bg-blue-500 hover:bg-blue-400 w-full text-white font-semibold py-2 transition-colors"
              >
                <Gem className="h-4 w-4" />
                <span>Buy More Gems</span>
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
} 